import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  concatMap,
  defer,
  finalize,
  first,
  map,
  Observable,
  of,
  takeUntil,
  tap,
} from 'rxjs';

import { NotificationService, syncSelect } from '@blaze-shared/platform-data-access';
import { PageContext } from '@blaze-shared/projects-domain';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { isBlazeTableSchema, isDatasetSchema } from '@blaze-shared/schema-domain';

import * as runtimeSelectors from '../../runtime/runtime.selectors';
import { logPageContext } from '../logger';

import { PageContextCommandService } from './page-context-command.service';
import { PageContextQueryService } from './page-context-query.service';
import { PageContextSessionStorageService } from './page-context-session-storage.service';
import { PreparePageContextDataService } from './prepare-page-context-data.service';

const MISSING_DATA_ROW_MESSAGE =
  'Data row specified in URL was not found. It may have been removed or the URL is outdated';

class DataRowMissing extends Error {
  override readonly name = 'DataRowMissing';
}

@Injectable({
  providedIn: 'root',
})
export class InitializePageContextService {
  private readonly actions$ = inject(Actions);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly preparePageContextDataService = inject(PreparePageContextDataService);
  private readonly pageContextSessionStorageService = inject(PageContextSessionStorageService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly pageContextSelectorService = inject(PageContextQueryService);
  private readonly notificationService = inject(NotificationService);
  private readonly pageContextCommandService = inject(PageContextCommandService);

  initializePageContextForProject(projectId: string): Observable<any> {
    return defer(() => {
      // Set the Page Context to loading state

      this.pageContextCommandService.initializationStart();
      // Wait for the pages to be loaded (even for new projects there should be at least one page)
      return this.store.select(runtimeSelectors.selectRuntimePages).pipe(
        first((pages) => Object.keys(pages).length > 0),
        // When the pages are loaded, we can initialize the page context
        concatMap(() => this.processPageContextInitialization(projectId)),
        catchError((err) => this.handleInitializationError(err)),
        finalize(() => this.pageContextCommandService.markAsLoaded()),
      );
    });
  }

  disposePageContext(): void {
    this.pageContextCommandService.reset();
  }

  private processPageContextInitialization(projectId: string): Observable<undefined> {
    const pageId = syncSelect(this.store, runtimeSelectors.selectPageId);
    const page = syncSelect(this.store, runtimeSelectors.selectPageById(pageId));
    const rowId = this.pageContextSelectorService.getCurrentPageRowIdSync();
    const sessionStoragePageContexts =
      this.pageContextSessionStorageService.getAllFromSessionStorage(projectId);

    this.dispatchSessionStorageInitialization(sessionStoragePageContexts);

    if (this.checkIfPageContextPresent(sessionStoragePageContexts)) {
      return of(undefined);
    }

    return this.tryInitializeFromUrl(page, rowId, pageId);
  }

  private dispatchSessionStorageInitialization(
    sessionStoragePageContexts: Record<string, PageContext>,
  ): void {
    this.pageContextCommandService.initializeFromSessionStorage(sessionStoragePageContexts);

    logPageContext('info', 'InitializePageContextService', 'initializePageContext', (tag) =>
      console.log(`${tag} 📄 Page Context - session storage populated`, {
        sessionStoragePageContexts,
      }),
    );
  }

  private tryInitializeFromUrl(
    page: { mainDatasetId?: string } | undefined,
    rowId: string | undefined | null,
    pageId: string,
  ): Observable<undefined> {
    logPageContext('info', 'InitializePageContextService', 'initializePageContext', (tag) =>
      console.log(`${tag} 📄 Page Context - trying to populate from URL param`, {
        mainDatasetId: page?.mainDatasetId,
        urlDataRowId: rowId,
      }),
    );

    if (!page?.mainDatasetId || !rowId) {
      return of(undefined);
    }

    return this.buildPageContext(page.mainDatasetId, rowId, pageId).pipe(
      tap((pageContext) => this.pageContextCommandService.setPageContext(pageContext)),
      map(() => undefined),
    );
  }

  private handleInitializationError(err: Error): Observable<undefined> {
    if (err instanceof DataRowMissing) {
      logPageContext('warn', 'PageContextFacade', 'initializePageContext', (tag) =>
        console.warn(`${tag} 📄 Page Context - data row specified in URL was not found`, err),
      );
      this.notificationService.error(MISSING_DATA_ROW_MESSAGE);
      return of(undefined);
    }

    logPageContext('error', 'PageContextFacade', 'initializePageContext', (tag) =>
      console.error(`${tag} 📄 Page Context - failed to initialize!`, err),
    );
    this.notificationService.error('Failed to initialize page context.');
    throw err;
  }

  private buildPageContext(
    datasetId: string,
    dataRowId: string,
    pageId: string,
  ): Observable<PageContext> {
    // Fetch the data row from the dataset and initialize Page Context
    return combineLatest({
      data: this.datasetRowsService.getRowById(datasetId, dataRowId).pipe(map((rows) => rows[0])),
      schema: this.schemaService.obtainSchema(datasetId),
    }).pipe(
      // Stop fetching if navigation happens in the meantime to avoid race conditions
      takeUntil(this.actions$.pipe(ofType(routerNavigatedAction))),
      map(({ data, schema }) => {
        if (!isDatasetSchema(schema) && !isBlazeTableSchema(schema)) {
          throw new Error(
            `InitializePageContextService:initializePageContext - schema is not of expected type`,
          );
        }
        if (!data) {
          throw new DataRowMissing(
            `InitializePageContextService:initializePageContext - data row with id ${dataRowId} not found in dataset ${datasetId}`,
          );
        }

        return this.preparePageContextDataService.preparePageContext({
          data,
          schema,
          pageId,
          formContext: undefined,
          isEmptyishRow: false,
        });
      }),
    );
  }

  private checkIfPageContextPresent(
    sessionStoragePageContexts: Record<string, PageContext>,
  ): boolean {
    const currentPageContextId = this.pageContextSelectorService.getCurrentPageContextIdSync();

    const currentPageContext = currentPageContextId
      ? sessionStoragePageContexts[currentPageContextId]
      : undefined;

    logPageContext('info', 'InitializePageContextService', 'checkIfPageContextPresent', (tag) =>
      console.log(`${tag} 📄 Page Context`, {
        currentPageContextId,
        pageContextFromUrl: currentPageContext,
      }),
    );

    return !!currentPageContext;
  }
}
